import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./styles.css";

import { DropdownV2, MultiDropdownV2 } from "../../FormControlsV2";
import { useFilter } from "../../../hooks/useFilter";
import {
  FilterV2Selection,
  selectionToIdStrings,
  useFilterContextV2,
} from "../../../contexts/FilterContextV2";
import {
  IOptionProps,
  IOptionsProps,
} from "../../../@types/reactSelect/IOption";
import { microAreaService } from "../../../services/microAreaService";
import { sortBy } from "../../../utils/arrays/sortBy";
import { batchService } from "../../../services/batchService";
import { getCenterCoords } from "../../../utils/leaflet/getCenterCoords";
import { ICoords } from "../../../@types/GoogleMaps/ICoord";
import { IBatch } from "../../../@types/API/IBatches";
import { IFarm } from "../../../@types/API/IFarm";
import { useTranslation } from "react-i18next";

type MinimalSearchProps = {
  placeHolder?: string;
  defaultOption?: IOptionProps;
};

export type FilterDataPolygonsV2Props = {
  searchPastoOuLoteProps?: MinimalSearchProps;
};

function isSelectionEmpty(v: FilterV2Selection): boolean {
  if (v == null) return true;
  return Array.isArray(v) && v.length === 0;
}

/** Remove retiros cuja fazenda não está mais em farmIds (evita seleção órfã). */
function pruneAreaSelection(
  prev: IOptionProps | IOptionProps[] | null,
  farmIds: string[],
  areas: IArea[],
  isDashboard: boolean,
): IOptionProps | IOptionProps[] | null {
  if (prev == null) return null;
  const farmSet = new Set(farmIds);
  const isAreaValid = (opt: IOptionProps) => {
    const area = areas.find((a) => String(a.id) === String(opt.value));
    if (!area?.farm?.id) return false;
    return farmSet.has(String(area.farm.id));
  };
  if (isDashboard) {
    const arr = Array.isArray(prev) ? prev : [prev];
    const filtered = arr.filter(isAreaValid);
    return filtered.length > 0 ? filtered : null;
  }
  // MAPA: seleção única; se veio do dashboard com multi-retiro, mantém o primeiro válido
  if (Array.isArray(prev)) {
    const filtered = prev.filter(isAreaValid);
    return filtered.length > 0 ? filtered[0] : null;
  }
  return isAreaValid(prev) ? prev : null;
}

function microAreaToOption(m: IMicroArea): IOptionProps {
  const prefix =
    m.farm && m.area ? `${m.farm.initials}-${m.area.initials}-` : "";
  return {
    label: `${prefix}${m.initials}`,
    value: m.id,
    info: m,
  };
}

export function FilterDataPolygonsV2(props: FilterDataPolygonsV2Props) {
  const { t } = useTranslation();
  const { searchPastoOuLoteProps } = props;

  const {
    sideBarOptionSelected,
    dashboard,
    responseFarms,
    responseArea,
    setMicroArea,
    setBatchFilter,
  } = useFilter();

  const {
    selectedFarms: selectedFarmsUi,
    setSelectedFarms: setSelectedFarmsUi,
    selectedAreas: selectedAreasUi,
    catalogsReady,
    setSelectedAreas: setSelectedAreasUi,
    catalogMicroAreas: microAreasFromFarms,
    setCatalogMicroAreas: setMicroAreasFromFarms,
    catalogBatches: batchesFromFarms,
    setCatalogBatches: setBatchesFromFarms,
  } = useFilterContextV2();

  const [pastoOuLoteSelected, setPastoOuLoteSelected] =
    useState<IOptionProps | null>(
      searchPastoOuLoteProps?.defaultOption ?? null,
    );

  const skipPastoResetAfterAreaSyncRef = useRef(false);

  useEffect(() => {
    if (searchPastoOuLoteProps?.defaultOption) {
      setPastoOuLoteSelected(searchPastoOuLoteProps.defaultOption);
    }
  }, [searchPastoOuLoteProps?.defaultOption]);

  const isMultiFarm =
    sideBarOptionSelected === "DASHBOARD" &&
    (dashboard === "CATTLE_MAP" ||
      dashboard === "CARBON" ||
      dashboard === "SUPPLEMENTATION");
  const isDashboard = sideBarOptionSelected === "DASHBOARD";

  const farmOptions: IOptionsProps = useMemo(
    () =>
      responseFarms.map((farm) => ({
        label: farm.name,
        value: farm.id,
        info: farm,
      })),
    [responseFarms],
  );

  useEffect(() => {
    if (sideBarOptionSelected !== "MAPA") return;
    if (!isSelectionEmpty(selectedFarmsUi) || farmOptions.length === 0) return;
    setSelectedFarmsUi(farmOptions[0]);
  }, [sideBarOptionSelected, selectedFarmsUi, farmOptions]);

  useEffect(() => {
    if (isMultiFarm) return;
    setSelectedFarmsUi((prev) => {
      if (Array.isArray(prev)) {
        return prev.length > 0 ? prev[0] : null;
      }
      return prev;
    });
  }, [isMultiFarm]);

  const selectedFarmsForContext = selectedFarmsUi;

  const selectedFarmIds = useMemo(
    () => selectionToIdStrings(selectedFarmsForContext),
    [selectedFarmsForContext],
  );

  const areasOptions: IOptionsProps = useMemo(() => {
    if (selectedFarmIds.length === 0) return [];
    const idSet = new Set(selectedFarmIds);
    return responseArea
      .filter((area) => {
        const farmId = area.farm?.id;
        if (farmId === undefined || farmId === null) return false;
        return idSet.has(String(farmId));
      })
      .map((area) => ({
        label: area.name ?? "",
        value: area.id,
        info: area,
      }));
  }, [responseArea, selectedFarmIds]);

  const selectedAreasForContext = selectedAreasUi;

  function handleFarmsMultiChange(opts: IOptionProps[]) {
    setSelectedFarmsUi(opts.length > 0 ? opts : null);
  }

  function handleFarmsSingleChange(opt: IOptionProps | null) {
    setSelectedFarmsUi(opt);
  }

  function handleAreasMultiChange(opts: IOptionProps[]) {
    setSelectedAreasUi(opts.length > 0 ? opts : null);
  }

  function handleAreasSingleChange(opt: IOptionProps | null) {
    setSelectedAreasUi(opt);
  }

  const farmsMultiValue = useMemo<IOptionProps[]>(() => {
    if (selectedFarmsUi == null) return [];
    return Array.isArray(selectedFarmsUi) ? selectedFarmsUi : [selectedFarmsUi];
  }, [selectedFarmsUi]);

  const farmsSingleValue = useMemo<IOptionProps | null>(() => {
    if (selectedFarmsUi == null) return null;
    return Array.isArray(selectedFarmsUi)
      ? (selectedFarmsUi[0] ?? null)
      : selectedFarmsUi;
  }, [selectedFarmsUi]);

  const areasMultiValue = useMemo<IOptionProps[]>(() => {
    if (selectedAreasUi == null) return [];
    return Array.isArray(selectedAreasUi) ? selectedAreasUi : [selectedAreasUi];
  }, [selectedAreasUi]);

  const areasSingleValue = useMemo<IOptionProps | null>(() => {
    if (selectedAreasUi == null) return null;
    return Array.isArray(selectedAreasUi)
      ? (selectedAreasUi[0] ?? null)
      : selectedAreasUi;
  }, [selectedAreasUi]);

  useEffect(() => {
    if (!catalogsReady) return;
    setSelectedAreasUi((prev) =>
      pruneAreaSelection(prev, selectedFarmIds, responseArea, isDashboard),
    );
  }, [selectedFarmIds.join(","), responseArea, isDashboard, catalogsReady]);

  useEffect(() => {
    if (selectedFarmIds.length === 0) {
      setMicroAreasFromFarms([]);
      return;
    }

    if (sideBarOptionSelected === "DASHBOARD") {
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    Promise.all(
      selectedFarmIds.map((fid) =>
        microAreaService.getMicroAreasByFarm(Number(fid), { signal }),
      ),
    )
      .then((chunks) => {
        if (signal.aborted) return;
        const merged = chunks.flat();
        const byId = new Map<number, IMicroArea>();
        merged.forEach((m) => byId.set(m.id, m));
        setMicroAreasFromFarms(Array.from(byId.values()));
      })
      .catch(() => {
        if (signal.aborted) return;
        setMicroAreasFromFarms([]);
      });

    Promise.all(
      selectedFarmIds.map((fid) =>
        batchService.getBatchesByFarm(Number(fid), { signal }),
      ),
    )
      .then((chunks) => {
        if (signal.aborted) return;
        const merged = chunks.flat();
        setBatchesFromFarms(merged);
      })
      .catch(() => {
        if (signal.aborted) return;
        setBatchesFromFarms([]);
      });

    return () => {
      controller.abort();
    };
  }, [selectedFarmIds, sideBarOptionSelected]);

  const selectedAreaIdSet = useMemo(() => {
    if (!selectedAreasForContext) return new Set<string>();
    const opts = Array.isArray(selectedAreasForContext)
      ? selectedAreasForContext
      : [selectedAreasForContext];
    return new Set(opts.map((o) => String(o.value)));
  }, [selectedAreasForContext]);

  const pastoOuLoteSearchOptions: IOptionsProps = useMemo(() => {
    const microList =
      selectedAreaIdSet.size === 0
        ? microAreasFromFarms
        : microAreasFromFarms.filter((m) =>
            selectedAreaIdSet.has(String(m.area_id)),
          );
    const batchList =
      selectedAreaIdSet.size === 0
        ? batchesFromFarms
        : batchesFromFarms.filter((b) =>
            selectedAreaIdSet.has(String(b.area.id)),
          );
    const microOpts = microList.map((m) => {
      const base = microAreaToOption(m);
      return {
        ...base,
        value: `microArea:${m.id}`,
      };
    });
    const batchOpts = batchList.map((b) => ({
      label: `${b.farm.initials}-${b.area.initials}-${b.initials}`,
      value: `batch:${b.id}`,
      info: b,
    }));
    return [...microOpts, ...batchOpts].sort(sortBy("label"));
  }, [microAreasFromFarms, batchesFromFarms, selectedAreaIdSet]);

  const exportPastoOuLoteOption = useCallback(
    (option: IOptionProps | undefined) => {
      if (!option?.value) {
        setMicroArea(undefined);
        setBatchFilter(undefined);
        return;
      }
      const full =
        pastoOuLoteSearchOptions.find(
          (o) => String(o.value) === String(option.value),
        ) ?? option;
      const raw = String(full.value);
      const info = full.info;

      const syncRetiroFromAreaId = (areaId: string | number) => {
        const area = responseArea.find((a) => String(a.id) === String(areaId));
        if (!area) return;
        const nextId = String(area.id);
        const cur = selectedAreasUi;
        const curId =
          cur == null
            ? ""
            : Array.isArray(cur)
              ? [...cur.map((o) => String(o.value))].sort().join(",")
              : String(cur.value);
        if (curId === nextId) return;
        skipPastoResetAfterAreaSyncRef.current = true;
        setSelectedAreasUi({
          label: area.name ?? "",
          value: area.id,
          info: area,
        });
      };

      if (raw.startsWith("microArea:") && info) {
        const m = info as IMicroArea;
        setBatchFilter(undefined);
        const mar = m.micro_area_route;
        setMicroArea({
          id: String(m.id),
          areaId: String(m.area_id),
          coords: m.map_coords,
          initials: m.initials,
          microAreaRoute: {
            batch: mar?.batch
              ? {
                  id: String(mar.batch.id),
                  initials: String(mar.batch.initials),
                }
              : { id: "", initials: "" },
            current: mar?.current_micro_area ?? false,
          },
        });
        syncRetiroFromAreaId(m.area_id);
        return;
      }

      if (raw.startsWith("batch:") && info) {
        const b = info as IBatch;
        setMicroArea({
          id: String(b.current_micro_area.id),
          areaId: String(b.area.id),
          coords: b.current_micro_area.map_coords as ICoords,
          initials: b.initials,
          microAreaRoute: {
            batch: { id: String(b.id), initials: b.initials },
            current: true,
          },
        });
        setBatchFilter({
          areaId: String(b.area.id),
          capacityRate: b.batch_capacity_rate,
          coords: getCenterCoords(b.current_micro_area.map_coords),
          farmId: String(b.farm.id),
          id: String(b.id),
          initials: b.initials,
          microAreaId: String(b.current_micro_area.id),
        });
        syncRetiroFromAreaId(b.area.id);
      }
    },
    [
      pastoOuLoteSearchOptions,
      responseArea,
      selectedAreasUi,
      setMicroArea,
      setBatchFilter,
      setSelectedAreasUi,
    ],
  );

  const selectedAreasKey = useMemo(() => {
    if (!selectedAreasUi) return "";
    const opts = Array.isArray(selectedAreasUi)
      ? selectedAreasUi
      : [selectedAreasUi];
    return [...opts.map((o) => String(o.value))].sort().join(",");
  }, [selectedAreasUi]);

  useEffect(() => {
    if (skipPastoResetAfterAreaSyncRef.current) {
      skipPastoResetAfterAreaSyncRef.current = false;
      return;
    }
    setPastoOuLoteSelected(null);
    setMicroArea(undefined);
    setBatchFilter(undefined);
  }, [
    selectedFarmIds.join(","),
    selectedAreasKey,
    setMicroArea,
    setBatchFilter,
  ]);

  const hasRetiroUi = !isSelectionEmpty(selectedAreasUi);
  const hasFarmUi = !isSelectionEmpty(selectedFarmsUi);

  const managerNameDisplay = useMemo(() => {
    if (hasRetiroUi) {
      const areaOpt = Array.isArray(selectedAreasUi)
        ? selectedAreasUi[0]
        : selectedAreasUi;
      if (!areaOpt) return "";
      const area =
        responseArea.find((a) => String(a.id) === String(areaOpt.value)) ??
        (areaOpt.info as IArea | undefined);
      return area?.manager_user?.name ?? "";
    }
    if (hasFarmUi) {
      const farmOpt = Array.isArray(selectedFarmsUi)
        ? selectedFarmsUi[0]
        : selectedFarmsUi;
      if (!farmOpt) return "";
      const farm =
        responseFarms.find((f) => String(f.id) === String(farmOpt.value)) ??
        (farmOpt.info as IFarm | undefined);
      return farm?.owner_user?.name ?? "";
    }
    return "";
  }, [
    hasRetiroUi,
    hasFarmUi,
    selectedAreasUi,
    selectedFarmsUi,
    responseArea,
    responseFarms,
  ]);

  const ownerRoleLabel = useMemo(() => {
    if (hasRetiroUi) return t("polygonFilter.foreman");
    if (hasFarmUi) return t("polygonFilter.manager");
    return t("polygonFilter.responsible");
  }, [hasRetiroUi, hasFarmUi, t]);

  return (
    <section className="filterpolygons__container">
      {isMultiFarm ? (
        <MultiDropdownV2
          name="filter-farms"
          options={farmOptions}
          value={farmsMultiValue}
          onChange={handleFarmsMultiChange}
          placeholder={t("polygonFilter.farms")}
          emptyMessage={t("polygonFilter.allFarms")}
          menuMount="static"
        />
      ) : (
        <DropdownV2
          name="filter-farms"
          options={farmOptions}
          value={farmsSingleValue}
          onChange={handleFarmsSingleChange}
          placeholder={t("polygonFilter.farms")}
          emptyMessage={t("polygonFilter.allFarms")}
          menuMount="static"
        />
      )}

      {isDashboard ? (
        <MultiDropdownV2
          name="filter-areas"
          options={areasOptions}
          value={areasMultiValue}
          onChange={handleAreasMultiChange}
          placeholder={t("polygonFilter.allAreas")}
          emptyMessage={t("polygonFilter.allAreas")}
          clearOptionLabel={t("polygonFilter.allAreas")}
          menuMount="static"
        />
      ) : (
        <DropdownV2
          name="filter-areas"
          options={areasOptions}
          value={areasSingleValue}
          onChange={handleAreasSingleChange}
          placeholder={t("polygonFilter.allAreas")}
          emptyMessage={t("polygonFilter.allAreas")}
          clearOptionLabel={t("polygonFilter.allAreas")}
          menuMount="static"
        />
      )}

      {sideBarOptionSelected !== "DASHBOARD" && (
        <>
          <div className="filterpolygons__owner--container">
            <h6>{managerNameDisplay}</h6>
          </div>

          <DropdownV2
            name="filter-pasto-lote"
            options={pastoOuLoteSearchOptions}
            value={pastoOuLoteSelected}
            placeholder={
              searchPastoOuLoteProps?.placeHolder ?? t("polygonFilter.searchPaddockBatch")
            }
            searchable
            emptyMessage={t("polygonFilter.noPaddockBatch")}
            menuMount="static"
            onChange={(opt) => {
              setPastoOuLoteSelected(opt);
              exportPastoOuLoteOption(opt ?? undefined);
            }}
          />
        </>
      )}
    </section>
  );
}
